import React from 'react';
import {Admin, fetchUtils, Resource, CustomRoutes} from 'react-admin';
import {Route} from 'react-router-dom';
import statamicMuiTheme from './themes/statamicMuiTheme';
import authProvider from './auth/provider';
import Dashboard from './dashboard';
import springDataRest from "./dataprovider/springDataRest";
import i18nProvider from './i18nProvider';

import CharityList from "./charity/CharityListStatamic";
import {CharityCreateStatamic as CharityCreate, CharityEditStatamic as CharityEdit} from "./charity/CharityEditStatamic";
// project imports
import {
    AppErrorsIcon, BellDashboardIcon,
    CharityDashboardIcon,
    ChildDashboardIcon, ChildPostsStatsIcon, ExportSettingsIcon,
    GuardianDashboardIcon, MailLogsIcon, MailSettingsIcon,
    PostsDashboardIcon, RNActionsIcon, UserActionLogsIcon,
    VolunteersDashboardIcon, MetadataDefinitionIcon, ModerationQueueIcon
} from "./icons/icons";
import CharityShow from "./charity/CharityShowStatamic";
import StatamicLayout from "./components/statamic/StatamicLayout";
import hasPermission from "./auth/hasPermission";
import VolunteerList from "./volunteer/VolunteerListStatamic";
import {VolunteerCreateStatamic as VolunteerCreate, VolunteerEditStatamic as VolunteerEdit} from "./volunteer/VolunteerEditStatamic";
import VolunteerShow from "./volunteer/VolunteerShowStatamic";
import UmbrellaAdminList from "./umbrellaAdmin/UmbrellaAdminListStatamic";
import UmbrellaAdminShow from "./umbrellaAdmin/UmbrellaAdminShowStatamic";
import {
    UmbrellaAdminCreateStatamic as UmbrellaAdminCreate,
    UmbrellaAdminEditStatamic as UmbrellaAdminEdit,
} from "./umbrellaAdmin/UmbrellaAdminEditStatamic";
import ChildList from "./children/ChildListStatamic";
import ChildShow from "./children/ChildShowStatamic";
import {ChildCreateStatamic as ChildCreate, ChildEditStatamic as ChildEdit} from "./children/ChildEditStatamic";
import GuardianList from "./guardian/GuardianListStatamic";
import {GuardianCreateStatamic as GuardianCreate, GuardianEditStatamic as GuardianEdit} from "./guardian/GuardianEditStatamic";
import GuardianShow from "./guardian/GuardianShowStatamic";
import ActivityList from "./activity/ActivityListStatamic";
import PostList from "./posts/PostListStatamic";
import PostShow from "./posts/PostShowStatamic";
import {PostCreateStatamic as PostCreate, PostEditStatamic as PostEdit} from "./posts/PostEditStatamic";
import ChildUpdatePostStatsList from "./childUpdatePostStats/ChildUpdatePostStatsListStatamic";
import MailSettingsList from "./mail/MailSettingsListStatamic";
import {MailSettingsCreateStatamic as MailSettingsCreate, MailSettingsEditStatamic as MailSettingsEdit} from "./mail/MailSettingsEditStatamic";
import MailSettingsShow from "./mail/MailSettingsShowStatamic";
import MailLogsList from "./mailLogs/MailLogsListStatamic";
import MailLogsShow from "./mailLogs/MailLogsShowStatamic";
import ExportSettingsList from "./exportSettings/ExportSettingsListStatamic";
import {ExportSettingsCreateStatamic as ExportSettingsCreate, ExportSettingsEditStatamic as ExportSettingsEdit} from "./exportSettings/ExportSettingsEditStatamic";
import AppErrorsList from "./appErrors/AppErrorsListStatamic";
import AppErrorsShow from "./appErrors/AppErrorsShowStatamic";
import UserActionLogsList from "./userActionLogs/UserActionLogsListStatamic";
import UserActionLogsShow from "./userActionLogs/UserActionLogsShowStatamic";
import UmbrellaResource, {UmbrellaOwnResource} from "./umbrella/UmbrellaResource";
import RNActionList from "./rnActions/RNActionListStatamic";
import {RNActionCreateStatamic as RnActionCreate, RNActionEditStatamic as RnActionEdit} from "./rnActions/RNActionEditStatamic";
import MetadataDefinitionList from "./metadata/MetadataDefinitionListStatamic";
import {MetadataDefinitionCreateStatamic as MetadataDefinitionCreate, MetadataDefinitionEditStatamic as MetadataDefinitionEdit} from "./metadata/MetadataDefinitionEditStatamic";
import MetadataDefinitionShow from "./metadata/MetadataDefinitionShowStatamic";
import ModerationQueue from "./moderation/ModerationQueueStatamic";
import ProfilePage from "./profile/ProfilePage";

const httpClient = (url, options = {}) => {
    if (!options.headers) {
        options.headers = new Headers({Accept: 'application/json'});
    }
    const accessToken = localStorage.getItem('accessToken');
    options.headers.set('Authorization', 'Bearer ' + accessToken);
    return fetchUtils.fetchJson(url, options);
};

const dataProvider = springDataRest(`${process.env.REACT_APP_API_URL}/api/v1`, httpClient);

const App = () => {
    // Resource labels are stored as stable English KEYS (not pre-translated). The sidebar menu
    // and command palette translate them at render time via useTranslate(), so they re-translate
    // live when the locale changes. Pre-translating here froze the label at app-init locale.
    return (
        <Admin
            dashboard={Dashboard}
            i18nProvider={i18nProvider}
            dataProvider={dataProvider}
            authProvider={authProvider}
            layout={StatamicLayout}
            theme={statamicMuiTheme}
        >
            {permissions => (
                <>
                    {hasPermission(permissions, ['*'], 'umbrellas')
                        ? <Resource {...UmbrellaResource} />
                        : hasPermission(permissions, ['show'], 'umbrellas') &&
                            <Resource {...UmbrellaOwnResource} />
                    }

                    {hasPermission(permissions, ['list'], 'umbrellaAdmins') && (
                        <Resource
                            options={{label: 'Umbrella Admins'}}
                            name="umbrellaAdmins"
                            icon={VolunteersDashboardIcon}
                            list={UmbrellaAdminList}
                            create={hasPermission(permissions, ['create'], 'umbrellaAdmins') ? UmbrellaAdminCreate : null}
                            edit={hasPermission(permissions, ['edit'], 'umbrellaAdmins') ? UmbrellaAdminEdit : null}
                            show={hasPermission(permissions, ['show'], 'umbrellaAdmins') ? UmbrellaAdminShow : null}
                        />
                    )}

                    <Resource options={{label: 'Charities'}} name="charities" icon={CharityDashboardIcon}
                              list={hasPermission(permissions, ['list'], 'charities') ? CharityList : null}
                              create={hasPermission(permissions, ['create'], 'charities') ? CharityCreate : null}
                              edit={hasPermission(permissions, ['edit'], 'charities') ? CharityEdit : null}
                              show={hasPermission(permissions, ['show'], 'charities') ? CharityShow : null}
                    />

                    <Resource options={{label: 'Volunteers'}} name="volunteers" icon={VolunteersDashboardIcon}
                              list={hasPermission(permissions, ['list'], 'volunteers') ? VolunteerList : null}
                              create={hasPermission(permissions, ['create'], 'volunteers') ? VolunteerCreate : null}
                              edit={hasPermission(permissions, ['edit'], 'volunteers') ? VolunteerEdit : null}
                              show={hasPermission(permissions, ['show'], 'volunteers') ? VolunteerShow : null}
                    />

                    {hasPermission(permissions, ['*'], 'children') && (
                        <Resource options={{label: 'Children'}} name="children" icon={ChildDashboardIcon}
                                  list={ChildList} show={ChildShow} edit={ChildEdit} create={ChildCreate}
                        />
                    )}

                    <Resource options={{label: 'Guardians'}} name="guardians" icon={GuardianDashboardIcon}
                              list={hasPermission(permissions, ['list'], 'guardians') ? GuardianList : null}
                              create={hasPermission(permissions, ['create'], 'guardians') ? GuardianCreate : null}
                              edit={hasPermission(permissions, ['edit'], 'guardians') ? GuardianEdit : null}
                              show={hasPermission(permissions, ['show'], 'guardians') ? GuardianShow : null}
                    />

                    {hasPermission(permissions, ['*'], 'posts') && (
                        <Resource options={{label: 'Posts'}} name="posts" icon={PostsDashboardIcon}
                                  list={PostList} show={PostShow} edit={PostEdit} create={PostCreate}
                        />
                    )}

                    {(permissions?.includes?.('umbrella') || permissions?.includes?.('admin')) && (
                        <Resource
                            options={{label: 'Moderation Queue'}}
                            name="posts-pending"
                            icon={ModerationQueueIcon}
                            list={ModerationQueue}
                        />
                    )}

                    {hasPermission(permissions, ['*'], 'childUpdatePostStats') && (
                        <Resource options={{label: 'Child post stats'}} name="childUpdatePostStats"
                                  list={ChildUpdatePostStatsList} icon={ChildPostsStatsIcon}
                        />
                    )}

                    {hasPermission(permissions, ['*'], 'mailSettings') && (
                        <Resource options={{label: 'Mail Settings'}} name="mailSettings"
                                  list={MailSettingsList} create={MailSettingsCreate} edit={MailSettingsEdit}
                                  show={MailSettingsShow} icon={MailSettingsIcon}
                        />
                    )}
                    {hasPermission(permissions, ['*'], 'mailLogs') &&
                        <Resource options={{label: 'Mail Logs'}} name="mailLogs"
                                  list={MailLogsList} show={MailLogsShow} icon={MailLogsIcon}
                        />
                    }

                    {hasPermission(permissions, ['*'], 'activities') && (
                        <Resource options={{label: 'Notifications'}} name="activities" list={ActivityList}
                        icon={BellDashboardIcon}/>
                    )}

                    {hasPermission(permissions, ['*'], 'exportSettings') && (
                        <Resource options={{label: 'Export Settings'}} name="exportSettings"
                                  list={ExportSettingsList} create={ExportSettingsCreate} edit={ExportSettingsEdit}
                                  show={ExportSettingsEdit} icon={ExportSettingsIcon}
                        />
                    )}

                    {(hasPermission(permissions, ['*'], 'metadata-definitions') ||
                      permissions?.includes?.('admin') ||
                      permissions?.includes?.('umbrella')) && (
                        <Resource
                            options={{label: 'Metadata Definitions'}}
                            name="metadata-definitions"
                            list={MetadataDefinitionList}
                            create={hasPermission(permissions, ['create'], 'metadata-definitions') || permissions?.includes?.('admin') || permissions?.includes?.('umbrella') ? MetadataDefinitionCreate : null}
                            edit={hasPermission(permissions, ['edit'], 'metadata-definitions') || permissions?.includes?.('admin') || permissions?.includes?.('umbrella') ? MetadataDefinitionEdit : null}
                            show={MetadataDefinitionShow}
                            icon={MetadataDefinitionIcon}
                        />
                    )}

                    {hasPermission(permissions, ['*'], 'rnActions') && (
                        <Resource options={{label: 'RN Actions'}} name="rnActions"
                                  list={RNActionList} create={RnActionCreate} edit={RnActionEdit}
                                  show={RnActionEdit} icon={RNActionsIcon}
                        />
                    )}

                    {hasPermission(permissions, ['*'], 'errors') &&
                        <Resource options={{label: 'App Errors'}} name="errors"
                                  list={AppErrorsList} show={AppErrorsShow} icon={AppErrorsIcon}
                        />}
                    {hasPermission(permissions, ['*'], 'userActionLogs') &&
                        <Resource options={{label: 'User Action Logs'}} name="userActionLogs"
                                  list={UserActionLogsList} show={UserActionLogsShow} icon={UserActionLogsIcon}
                        />}

                    {/* Custom Routes */}
                    <CustomRoutes>
                        <Route path="/profile" element={<ProfilePage />} />
                    </CustomRoutes>
                </>
            )}
        </Admin>
    );
}

export default App;

